// Microbe RTS — PvP wire protocol (single-engine input-lockstep).
//
// The sim is deterministic (seeded mulberry32, proven bit-identical WITHIN one JS engine), so PvP
// syncs INPUTS, not state. This file is the single source of truth for the JSON frames both the
// client and the relay speak, plus the lockstep tuning constants. Keep it pure shape + constants —
// no logic, no DOM. Every message is a JSON text frame carrying a discriminant `t`.
//
// Flow (see net/transport.ts + net/lockstep.ts for the client half, the relay DO for the server):
//   client → GET `${RELAY_URL}/join/:code?engine=<jsc|v8|other>`  (WebSocket upgrade)
//   server → both, when 2 joined: { t:'start', seed, side }
//   both, every tick:            { t:'input', tick, seq, cmds }   (relayed verbatim to the peer)
//   both, every CHECKSUM_EVERY:  { t:'sum',   tick, hash }        (desync canary)
//   server → peer on close:      { t:'peer_gone' }
//   server → 2nd joiner on mismatch: { t:'engine_mismatch' }

import type { Command, Side } from '../types.js';

/** JS engine family. Same-engine-only matches (iOS/WebKit is the golden path). */
export type Engine = 'jsc' | 'v8' | 'other';

// ---- Lockstep tuning ----
/** Fixed input delay, in ticks. A command issued while rendering tick T executes at T + D. */
export const D = 3;
/** Checksum canary cadence: every K ticks each client exchanges a state hash. */
export const CHECKSUM_EVERY = 300;
/** Fixed sim timestep (30 Hz). Passed to sim.update each lockstep tick. */
export const SIM_DT = 1 / 30;

/** Default relay origin. Overridable via `globalThis.__RELAY_URL` (tests point at ws://localhost:8787). */
const DEFAULT_RELAY_URL = 'https://microbe-relay.azbaghda.workers.dev';
const relayOverride = (globalThis as Record<string, unknown>).__RELAY_URL;
export const RELAY_URL: string = typeof relayOverride === 'string' ? relayOverride : DEFAULT_RELAY_URL;

// ---- Messages ----
/** Server → both clients once 2 have joined. Creator gets side 'you', joiner 'bot'. */
export interface StartMsg {
  t: 'start';
  seed: number; // uint32, generated by the DO via crypto.getRandomValues
  side: Side; // this recipient's side
}

/** Per-tick input frame. Emitted EVERY tick by each client (cmds is [] on idle ticks) so the peer
 *  knows the tick is complete. `tick` is the execTick the commands apply at. Relayed verbatim. */
export interface InputMsg {
  t: 'input';
  tick: number; // execTick these commands apply at (render tick + D)
  seq: number; // per-client monotonic counter (gap/dupe detection)
  cmds: Command[]; // submission-order commands; [] when idle
}

/** Desync canary: a state hash at a given tick, compared against the peer's hash for that tick. */
export interface SumMsg {
  t: 'sum';
  tick: number; // number of ticks executed when this hash was taken
  hash: string; // hashState() hex
}

/** Optional explicit desync broadcast (a client that detects a mismatch may notify its peer). */
export interface DesyncMsg {
  t: 'desync';
  tick: number;
}

/** Server → the surviving client when its peer's socket closes. */
export interface PeerGoneMsg {
  t: 'peer_gone';
}

/** Server → the 2nd joiner when its engine differs from the 1st joiner's (no mixed-engine matches). */
export interface EngineMismatchMsg {
  t: 'engine_mismatch';
}

/** Anything a client may SEND to the relay (relay forwards input/sum/desync verbatim to the peer). */
export type ClientMsg = InputMsg | SumMsg | DesyncMsg;

/** Anything a client may RECEIVE (server control frames + peer-relayed frames). */
export type ServerMsg =
  | StartMsg
  | InputMsg
  | SumMsg
  | DesyncMsg
  | PeerGoneMsg
  | EngineMismatchMsg;

/** Reasons a session can end (surfaced via onEnd). */
export type EndReason = 'desync' | 'peer_gone' | 'engine_mismatch' | 'error';
